/**
 * Standalone implementation of @optimgov/module-auth.
 *
 * The original package lived in the retired OptimGov SDK feed and delegated
 * SSO to the Hub platform. This local implementation keeps the exact same
 * public API (AuthProvider, useAuth, buildMsalConfig, AuthUser) but talks
 * directly to Microsoft Entra ID via @azure/msal-browser — any Entra tenant
 * configured through ENTRA_* env vars / window.__CONTRACTACIO_CONFIG__ works.
 *
 * Modes:
 *   - "mock": LOCAL_MODE development. Auth core is bypassed — useAuth
 *     (src/hooks/useAuth.ts) derives state from localStorage.local_role.
 *   - "msal": production. Redirect-based Entra login; access tokens for the
 *     configured API scope are attached by the auth bridge (apiUtils).
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import {
	PublicClientApplication,
	InteractionRequiredAuthError,
	type AccountInfo,
	type Configuration,
} from "@azure/msal-browser"

export interface AuthUser {
	userId: string
	email: string | null
	displayName: string | null
}

export interface ModuleAuthValue {
	isAuthenticated: boolean
	user: AuthUser | null
	isLoading: boolean
	getAccessToken: () => Promise<string>
	login: () => Promise<void>
	logout: () => Promise<void>
}

// Backwards-compatible alias with the SDK's exported name
export type AuthContextValue = ModuleAuthValue

export interface BuildMsalConfigParams {
	clientId?: string
	tenantId?: string
	/** Legacy Hub URL — accepted for call-site compatibility, unused standalone. */
	hubUrl?: string
	redirectUri?: string
	postLogoutRedirectUri?: string
	authority?: string
	knownAuthorities?: string[]
	apiScopes?: string[]
}

export interface MsalAuthConfig {
	msalConfiguration: Configuration
	apiScopes: { default: string[]; api: string[] }
}

/**
 * Build the MSAL configuration from runtime config / env vars.
 * Defaults target a standard Entra ID tenant; CIAM / External ID tenants can
 * override `authority` + `knownAuthorities` (ENTRA_AUTHORITY env vars).
 */
export function buildMsalConfig(params: BuildMsalConfigParams = {}): MsalAuthConfig {
	const clientId = params.clientId ?? ""
	const authority = params.authority
		|| `https://login.microsoftonline.com/${params.tenantId || "organizations"}`

	let knownAuthorities = (params.knownAuthorities ?? []).filter(Boolean)
	if (knownAuthorities.length === 0) {
		try {
			knownAuthorities = [new URL(authority).hostname]
		} catch {
			knownAuthorities = []
		}
	}

	const apiScopes = (params.apiScopes ?? []).filter(Boolean)
	const api = apiScopes.length > 0 ? apiScopes : (clientId ? [`api://${clientId}/access`] : [])

	const redirectUri = params.redirectUri
		|| (typeof window !== "undefined" ? `${window.location.origin}/` : "/")

	return {
		msalConfiguration: {
			auth: {
				clientId,
				authority,
				knownAuthorities,
				redirectUri,
				postLogoutRedirectUri: params.postLogoutRedirectUri || redirectUri,
			},
			cache: {
				// localStorage keeps the session across tabs and full reloads
				cacheLocation: "localStorage",
			},
		},
		apiScopes: {
			default: ["openid", "profile", "email", ...api],
			api,
		},
	}
}

const MOCK_VALUE: ModuleAuthValue = {
	isAuthenticated: false,
	user: null,
	isLoading: false,
	getAccessToken: async () => "",
	login: async () => {
		console.warn("[module-auth] login() is a no-op in mock mode — use the local role selector")
	},
	logout: async () => {},
}

const AuthContext = createContext<ModuleAuthValue>(MOCK_VALUE)

export interface AuthProviderProps {
	mode?: "mock" | "msal"
	/** Legacy hub/module distinction — accepted for compatibility, unused standalone. */
	context?: "hub" | "module"
	msalConfig?: MsalAuthConfig
	children?: React.ReactNode
}

function mapAccount(account: AccountInfo): AuthUser {
	return {
		userId: account.localAccountId,
		email: account.username || null,
		displayName: account.name ?? null,
	}
}

function MsalAuthProvider({ config, children }: { config: MsalAuthConfig; children?: React.ReactNode }) {
	const pcaRef = useRef<PublicClientApplication | null>(null)
	if (!pcaRef.current) {
		pcaRef.current = new PublicClientApplication(config.msalConfiguration)
	}
	const pca = pcaRef.current

	const [isLoading, setIsLoading] = useState(true)
	const [account, setAccount] = useState<AccountInfo | null>(null)

	useEffect(() => {
		let cancelled = false

		;(async () => {
			await pca.initialize()
			// Completes a pending redirect flow (returns null on a plain load)
			const redirectResult = await pca.handleRedirectPromise()
			if (redirectResult?.account) {
				pca.setActiveAccount(redirectResult.account)
			}
			const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null
			if (active) pca.setActiveAccount(active)
			if (!cancelled) {
				setAccount(active)
				setIsLoading(false)
			}
		})().catch((err) => {
			console.error("[module-auth] MSAL initialization failed:", err)
			if (!cancelled) {
				setAccount(null)
				setIsLoading(false)
			}
		})

		return () => {
			cancelled = true
		}
	}, [pca])

	const login = useCallback(async () => {
		await pca.loginRedirect({ scopes: config.apiScopes.default })
	}, [pca, config])

	const logout = useCallback(async () => {
		await pca.logoutRedirect({ account: pca.getActiveAccount() ?? undefined })
	}, [pca])

	const getAccessToken = useCallback(async (): Promise<string> => {
		const active = pca.getActiveAccount() ?? pca.getAllAccounts()[0]
		if (!active) throw new Error("[module-auth] getAccessToken called without an authenticated account")
		try {
			const result = await pca.acquireTokenSilent({ scopes: config.apiScopes.api, account: active })
			return result.accessToken
		} catch (err) {
			if (err instanceof InteractionRequiredAuthError) {
				// Silent renewal impossible (expired session / consent) — restart interactively
				await pca.acquireTokenRedirect({ scopes: config.apiScopes.api, account: active })
				return ""
			}
			throw err
		}
	}, [pca, config])

	const value = useMemo<ModuleAuthValue>(() => ({
		isAuthenticated: !!account,
		user: account ? mapAccount(account) : null,
		isLoading,
		getAccessToken,
		login,
		logout,
	}), [account, isLoading, getAccessToken, login, logout])

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function AuthProvider({ mode = "msal", msalConfig, children }: AuthProviderProps) {
	if (mode === "msal" && msalConfig?.msalConfiguration.auth.clientId) {
		return <MsalAuthProvider config={msalConfig}>{children}</MsalAuthProvider>
	}
	// Mock mode (LOCAL_MODE) or missing MSAL config — inert provider
	return <AuthContext.Provider value={MOCK_VALUE}>{children}</AuthContext.Provider>
}

export function useAuth(): ModuleAuthValue {
	return useContext(AuthContext)
}
